import React, { useEffect, useState, forwardRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Eye,
  EyeOff,
  KeyRound,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Smartphone,
  Trash2,
  UserRound,
  Users,
  UserX,
} from 'lucide-react'

import LegacyButton from '../components/Button'
import Input from '../components/Input'
import Loading from '../components/Loading'
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  IconButton,
  IconChip,
  KeyValue,
  Modal,
  Money,
  Notice,
  Page,
  PersonCell,
  SelectInput,
  StatusPill,
  Tabs,
  TextInput,
} from '../ui'
import AddBox from '@mui/icons-material/AddBox'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Check from '@mui/icons-material/Check'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Clear from '@mui/icons-material/Clear'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Edit from '@mui/icons-material/Edit'
import FilterList from '@mui/icons-material/FilterList'
import FirstPage from '@mui/icons-material/FirstPage'
import LastPage from '@mui/icons-material/LastPage'
import Remove from '@mui/icons-material/Remove'
import SaveAlt from '@mui/icons-material/SaveAlt'
import Search from '@mui/icons-material/Search'
import ViewColumn from '@mui/icons-material/ViewColumn'
import {
  addCustomer,
  fetchAttendance,
  getUserByID,
  removeCustomer,
  resetDeviceID as resetUserDeviceID,
  resetPassword,
  resignUser,
  updateUser,
} from '../actions/userActions'
import { getRoles } from '../actions/roleActions'
import {
  updateUser as updateAdminUser,
  getAdminByID,
  resetDeviceID as resetAdminDeviceID,
  resetPassword as resetAdminPassword,
} from '../actions/AdminAction'
import { listClubs } from '../actions/clubActions'
import {
  ADD_CUSTOMER_TO_USER_RESET,
  FETCH_ATTENDANCE_RESET,
  GET_USER_BY_ID_RESET,
  REMOVE_CUSTOMER_FROM_USER_RESET,
  RESET_DEVICE_ID_RESET,
  RESET_PASSWORD_RESET,
  RESIGN_USER_RESET,
  UPDATE_USER_RESET,
} from '../constants/userConstants'
import {
  GET_ADMIN_BY_ID_RESET,
} from '../constants/adminConstant'
import MaterialTable from '../components/MaterialTableAdapter'
import moment from 'moment'
import { hasPermission } from '../constants/rolePermissions'

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
}

const DISTANCE_OPTIONS = [
  { value: '0.1', label: '100 m' },
  { value: '0.2', label: '200 m' },
  { value: '0.3', label: '300 m' },
  { value: '0.4', label: '400 m' },
  { value: '0.5', label: '500 m' },
  { value: '0.6', label: '600 m' },
  { value: '0.7', label: '700 m' },
  { value: '0.8', label: '800 m' },
  { value: '0.9', label: '900 m' },
  { value: '1', label: '1 km' },
  { value: '100', label: 'No restriction' },
]

// joinedOn / lastLogin are stored as strings: epoch millis, ISO or "YYYY-MM-DD HH:mm:ss".
const formatWhen = (value, withTime = false) => {
  if (!value) return '—'
  const parsed = /^\d{10,}$/.test(String(value)) ? moment(Number(value)) : moment(value)
  if (!parsed.isValid()) return String(value)
  return parsed.format(withTime ? 'DD MMM YYYY, h:mm A' : 'DD MMM YYYY')
}

// Same rule as the Users list: logged in within 30 minutes and not force-logged-out since.
const isAdminOnline = (user) => {
  if (!user?.lastLogin) return false

  const lastLoginTime = new Date(user.lastLogin)
  const diffMinutes = (Date.now() - lastLoginTime.getTime()) / (1000 * 60)
  if (diffMinutes >= 30) return false

  if (user.lastForceLogout && new Date(user.lastForceLogout) > lastLoginTime) {
    return false
  }

  return true
}

const UserByID = ({ history }) => {
  const navigate = useNavigate();
  let params = useParams()
  let id = params.id.replace(':', '')

  const location = useLocation()
  const isAdmin = location.state?.isAdmin

  // * States
  const [name, setName] = useState('')
  const [deviceInfo, setDeviceInfo] = useState(null)
  const [roleName, setRoleName] = useState('')
  const [email, setEmail] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [ipAddress, setIpAddress] = useState('')
  const [distanceForCalling, setDistanceForCalling] = useState(0)
  const [status, setStatus] = useState(true)
  const [joinedOn, setJoinedOn] = useState('')
  const [resignedOn, setResignedOn] = useState('')
  const [rolesFromServer, setRolesFromServer] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [role, setRole] = useState('')
  const [calling, setCalling] = useState(false)
  const [passwordModal, setPasswordModal] = useState(false)
  const [openResignModal, setOpenResignModal] = useState(false)
  const [showCalling, setShowCalling] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSettingPassword, setIsSettingPassword] = useState(false)
  const [userCustomers, setUserCustomers] = useState([])
  const [deleteCustomerModal, setDeleteCustomerModal] = useState(false)
  const [deleteCustomerID, setDeleteCustomerID] = useState('')
  const [addCustomerModal, setAddCustomerModal] = useState(false)
  const [addCustomerName, setAddCustomerName] = useState('')
  const [customersFromServer, setCustomersFromServer] = useState([])
  const [customerOptions, setCustomerOptions] = useState([])
  const [attenance, setAttendance] = useState([])
  const [tasks, setTasks] = useState([])
  // * Attendance
  const [attendanceDetails, setAttendanceDetails] = useState(null)
  const [lineData, setLineData] = useState([])
  const [date, setDate] = useState(new Date())
  // * Tabs
  const [tab, setTab] = useState('profile')
  // Legacy panels (customers, attendance, tasks, locations) have no tab any more
  const [showCustomers] = useState(false)
  const [showAttendance] = useState(false)
  const [showTasks] = useState(false)
  const [showLocations] = useState(false)
  const [assignedClubs, setAssignedClubs] = useState([])
  // Club management modals
  const [deleteClubModal, setDeleteClubModal] = useState(false)
  const [deleteClubId, setDeleteClubId] = useState('')
  const [editClubsModal, setEditClubsModal] = useState(false)
  const [allClubs, setAllClubs] = useState([])
  const [selectedClubsForEdit, setSelectedClubsForEdit] = useState([])

  // * Affiliate Management
  const [referredBy, setReferredBy] = useState(null)
  const [affiliateOptions, setAffiliateOptions] = useState([])
  const [selectedAffiliate, setSelectedAffiliate] = useState('')
  const [loadingAffiliates, setLoadingAffiliates] = useState(false)
  const [assigningAffiliate, setAssigningAffiliate] = useState(false)
  const [removeAffiliateModal, setRemoveAffiliateModal] = useState(false)

  // * Roles
  const [taskDetailsOption, setTaskDetailsOption] = useState(false)
  const [updateUserOption, setUpdateUserOption] = useState(false)
  const [loading, setLoading] = useState(true)

  // * Initialization
  const dispatch = useDispatch()

  // * Check for auth
  const adminLogin = useSelector((state) => state.adminLogin)
  const userLogin = useSelector((state) => state.userLogin)
  const { loadingUserInfo, userInfo } = isAdmin ? adminLogin : userLogin

  // Fetch user data from API using Redux
  useEffect(() => {
    if (id) {
      if (isAdmin) {
        dispatch(getAdminByID(id))
      } else {
        dispatch(getUserByID(id))
      }
      dispatch(getRoles())
    }
  }, [dispatch, id, isAdmin])

  // Handle getUserByID response
  // Handle getUserByID response
  const getUserByIDInfo = useSelector((state) => state.getUserByIDInfo)
  const getAdminByIDInfo = useSelector((state) => state.getAdminByIDInfo)

  const { loadingGetUserByIDInfo, errorGetUserByIDInfo, getUserByIDData } = isAdmin
    ? getAdminByIDInfo || {}
    : getUserByIDInfo

  useEffect(() => {
    if (isAdmin) {
      dispatch({ type: GET_ADMIN_BY_ID_RESET })
    } else {
      dispatch({ type: GET_USER_BY_ID_RESET })
    }
    if (getUserByIDData) {
      setLoading(false)
      setName(getUserByIDData.name || '')
      setDeviceInfo(getUserByIDData.deviceInfo || null)
      setEmail(getUserByIDData.email || '') // Use actual email field
      setIdentifier(getUserByIDData.identifier || '')
      setIpAddress(getUserByIDData.ipAddress || '')
      setDistanceForCalling(getUserByIDData.distanceForCalling || 0)
      setRoleName(getUserByIDData.role?.name || '')
      setStatus(getUserByIDData.status ?? true)
      setJoinedOn(getUserByIDData.joinedOn || '')
      setResignedOn(getUserByIDData.resignedOn || '')
      setRole(getUserByIDData.role?._id || '')
      setRolesFromServer(getUserByIDData.roles || [])
      setCalling(getUserByIDData.calling || false)
      setUserCustomers(getUserByIDData.customers || [])
      setCustomersFromServer(getUserByIDData.customers || [])
      setAttendance(getUserByIDData.attendance || [])
      setTasks(getUserByIDData.tasks || [])
      setAssignedClubs(getUserByIDData.assignedClubs || [])
      // Affiliate
      setReferredBy(getUserByIDData.referredBy || null)
      setSelectedAffiliate(getUserByIDData.referredBy?._id || '')
    } else if (errorGetUserByIDInfo) {
      setLoading(false)
      if (errorGetUserByIDInfo.includes('Cast')) {
        navigate('/')
        return
      }
      toast(errorGetUserByIDInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [getUserByIDData, errorGetUserByIDInfo, navigate])

  // Check user permissions
  useEffect(() => {
    const storageKey = isAdmin ? 'adminInfo' : 'userInfo'
    const storedAuthValue = localStorage.getItem(storageKey)
    const userInfoFromStorage = storedAuthValue
      ? JSON.parse(storedAuthValue)
      : null;

    if (userInfoFromStorage?.role) {
      setTaskDetailsOption(userInfoFromStorage.role.tasksDetailsView)
      setUpdateUserOption(hasPermission(userInfoFromStorage.role, 'usersUpdate'))
      if (!hasPermission(userInfoFromStorage.role, 'usersView')) {
        toast('Not Authorized', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
        navigate('/')
      }
    }
  }, [navigate, isAdmin])

  useEffect(() => {
    if (userInfo?.role) {
      setTaskDetailsOption(userInfo.role.tasksDetailsView)
      setUpdateUserOption(hasPermission(userInfo.role, 'usersUpdate'))
      if (!hasPermission(userInfo.role, 'usersView')) {
        toast('Not Authorized', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
        navigate('/')
      }
    }
  }, [userInfo, navigate])

  // * Check for role
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { loadingGetRole, getRoleData } = getRoleInfo

  useEffect(() => {
    if (getRoleData) {
      setTaskDetailsOption(getRoleData.tasksDetailsView)
      setUpdateUserOption(hasPermission(getRoleData, 'usersUpdate'))
      if (!hasPermission(getRoleData, 'usersView')) {
        toast('Not Authorized', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
        navigate('/')
      }
    }
  }, [userInfo, getRoleData, navigate])


  useEffect(() => {
    console.log('Role Debug:', {
      role: role,
      roleName: roleName,
      roleOptions: roleOptions,
      getUserByIDData: getUserByIDData
    });
  }, [role, roleName, roleOptions, getUserByIDData]);

  // * Club list for edit modal
  const clubList = useSelector((state) => state.clubList)
  const { clubs = [] } = clubList || {}

  useEffect(() => {
    if (clubs && Array.isArray(clubs) && clubs.length > 0) {
      setAllClubs(clubs)
    }
  }, [clubs])

  // * Attendance
  const searchDate = () => {
    if (date) {
      dispatch(fetchAttendance(id, moment(date).format('YYYY-MM-DD')))
    } else {
      toast('Enter a valid date to search', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }

  const fetchAttendanceInfo = useSelector((state) => state.fetchAttendanceInfo)
  const { loadingFetchAttendance, errorFetchAttendance, fetchAttendanceData } =
    fetchAttendanceInfo

  useEffect(() => {
    dispatch({ type: FETCH_ATTENDANCE_RESET })
    if (fetchAttendanceData) {
      setAttendanceDetails(fetchAttendanceData)
    } else if (errorFetchAttendance) {
      toast(errorFetchAttendance, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [fetchAttendanceData, errorFetchAttendance])

  useEffect(() => {
    if (attendanceDetails) {
      let data = []
      attendanceDetails.attendance.locations.forEach((item) => {
        data.push({ lat: item.latitude, lng: item.longitude })
      })
      setLineData(data)
    }
  }, [attendanceDetails])

  // * Get All Roles (Global State)
  const getRolesInfo = useSelector((state) => state.getRolesInfo)
  const { getRolesData: allRolesData } = getRolesInfo

  const selectedRoleDetails =
    allRolesData?.find((item) => item._id === role) ||
    (getUserByIDData?.role?._id === role ? getUserByIDData.role : null)
  const adminRoleRequiresClubs =
    isAdmin && selectedRoleDetails
      ? selectedRoleDetails.isSuperAdmin !== true
      : false
  const adminClubScopeLabel = isAdmin
    ? selectedRoleDetails?.isSuperAdmin === true
      ? 'All clubs'
      : assignedClubs.length > 0
        ? `${assignedClubs.length} club(s)`
        : 'No clubs assigned'
    : null
  const adminDeviceId = isAdmin ? getUserByIDData?.deviceID || '' : ''
  const adminDeviceDetails =
    deviceInfo && Object.keys(deviceInfo).length > 0 ? deviceInfo : null
  const adminHasRegisteredDevice =
    isAdmin &&
    (Boolean(adminDeviceId) ||
      Boolean(adminDeviceDetails))
  const adminLoginAccessLabel = isAdmin
    ? selectedRoleDetails?.isSuperAdmin === true
      ? 'Web + App'
      : selectedRoleDetails?.webAccess === true &&
          selectedRoleDetails?.appAccess === true
        ? 'Web + App'
        : selectedRoleDetails?.webAccess === true
          ? 'Web only'
          : selectedRoleDetails?.appAccess === true
            ? 'App only'
            : 'Restricted'
    : null

  const refreshCurrentUser = () => {
    if (isAdmin) {
      dispatch(getAdminByID(id))
      return
    }

    dispatch(getUserByID(id))
  }

  useEffect(() => {
    if (getUserByIDData && getUserByIDData.role && allRolesData) {
      // Create role options including current role
      let data = [];

      // Add current role first
      data.push({
        id: getUserByIDData.role._id,
        title: getUserByIDData.role.name
      });

      // Add other roles from global state
      if (allRolesData && allRolesData.length > 0) {
        allRolesData.forEach((item) => {
          // Avoid duplicating current role
          if (item._id !== getUserByIDData.role._id) {
            data.push({ id: item._id, title: item.name });
          }
        });
      }

      setRoleOptions(data);
    }
  }, [getUserByIDData, allRolesData]);

  useEffect(() => {
    if (customersFromServer.length > 0) {
      let data = []
      customersFromServer.forEach((item) => {
        data.push({ id: item._id, title: item.name })
      })
      setCustomerOptions(data)
    }
  }, [customersFromServer])

  useEffect(() => {
    if (calling === true || calling == 'true') {
      setShowCalling(true)
    } else {
      setShowCalling(false)
    }
  }, [calling])

  // * Update User
  const updateUserHandler = () => {
    if (!name || !role || !email) {
      toast('All the fields are mandatory', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    if (
      isAdmin &&
      adminRoleRequiresClubs &&
      assignedClubs.length === 0 &&
      getUserByIDData?.role?._id !== role
    ) {
      toast('Assign at least one club before switching this admin to a club-scoped role', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2500,
      })
      return
    }
    if (isAdmin) {
      dispatch(
        updateAdminUser(
          id,
          name,
          role,
          email,
          assignedClubs
        )
      )
    } else {
      dispatch(
        updateUser(
          id,
          name,
          role,
          Boolean(calling),
          email,
          ipAddress,
          distanceForCalling
        )
      )
    }
  }

  const updateUserInfo = useSelector((state) => state.updateUserInfo)
  const updateAdminInfo = useSelector((state) => state.updateAdminInfo)

  const { loadingUpdateUserInfo, errorUpdateUserInfo, updateUserData } = isAdmin
    ? updateAdminInfo || {}
    : updateUserInfo

  useEffect(() => {
    dispatch({ type: UPDATE_USER_RESET })
    if (updateUserData) {
      toast('User updated successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setTimeout(() => {
        refreshCurrentUser()
      }, 1000)
    } else if (errorUpdateUserInfo) {
      toast(errorUpdateUserInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [updateUserData, errorUpdateUserInfo, dispatch, id])

  // * Reset Device ID
  const resetDeviceIDHandler = () => {
    if (isAdmin) {
      dispatch(resetAdminDeviceID(id))
      return
    }

    dispatch(resetUserDeviceID(id))
  }

  const resetDeviceIDInfo = useSelector((state) => state.resetDeviceIDInfo)
  const {
    loadingResetDeviceIDInfo,
    errorResetDeviceIDInfo,
    resetDeviceIDData,
  } = resetDeviceIDInfo

  useEffect(() => {
    dispatch({ type: RESET_DEVICE_ID_RESET })
    if (resetDeviceIDData) {
      toast('Device ID reset successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setTimeout(() => {
        refreshCurrentUser()
      }, 1000)
    } else if (errorResetDeviceIDInfo) {
      toast(errorResetDeviceIDInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [resetDeviceIDData, errorResetDeviceIDInfo, dispatch, id])

  // * Reset Password
  const openPasswordModal = () => {
    setNewPassword('')
    setShowPassword(false)
    setIsSettingPassword(false)
    setPasswordModal(true)
  }

  const submitPasswordHandler = () => {
    if (!newPassword || newPassword.trim().length < 6) {
      toast('Password must be at least 6 characters long', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }
    setIsSettingPassword(true)
    if (isAdmin) {
      dispatch(resetAdminPassword(id, newPassword.trim()))
    } else {
      dispatch(resetPassword(id, newPassword.trim()))
    }
  }

  const resetPasswordInfo = useSelector((state) => state.resetPasswordInfo)
  const {
    loadingResetPasswordInfo,
    errorResetPasswordInfo,
    resetPasswordData,
  } = resetPasswordInfo

  useEffect(() => {
    dispatch({ type: RESET_PASSWORD_RESET })
    if (resetPasswordData) {
      toast('Password updated successfully!', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setIsSettingPassword(false)
      setPasswordModal(false)
      setNewPassword('')
    } else if (errorResetPasswordInfo) {
      toast(errorResetPasswordInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setIsSettingPassword(false)
    }
  }, [resetPasswordData, errorResetPasswordInfo])

  const closePasswordModal = () => {
    setNewPassword('')
    setShowPassword(false)
    setIsSettingPassword(false)
    setPasswordModal(false)
  }

  // * Resign
  const resignHandler = () => {
    dispatch(resignUser(id))
  }

  const resignUserInfo = useSelector((state) => state.resignUserInfo)
  const { loadingResignUser, errorResignUser, resignUserData } = resignUserInfo

  useEffect(() => {
    dispatch({ type: RESIGN_USER_RESET })
    if (resignUserData) {
      toast(resignUserData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setOpenResignModal(false)
      setTimeout(() => {
        refreshCurrentUser()
      }, 1000)
    } else if (errorResignUser) {
      toast(errorResignUser, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [resignUserData, errorResignUser, dispatch, id])

  // * Tasks
  const headCellsTasks = [
    {
      field: 'taskID',
      title: 'Task ID',
      render: (rowData) =>
        taskDetailsOption ? (
          <Link
            className='font-bold text-md cursor-pointer text-blue-800 bg-blue-100 p-2 flex justify-center rounded'
            target='_blank'
            to={`/tasks/${rowData._id}`}
          >
            {rowData.taskID}
          </Link>
        ) : (
          <div className='font-bold text-md p-2 flex justify-center rounded bg-red-100 text-red-800'>
            <p className='text-sm'>{rowData.taskID}</p>
          </div>
        ),
    },
    {
      field: 'name',
      title: 'Name',
    },
    {
      field: 'type',
      title: 'Type',
    },
    {
      field: 'status',
      title: 'Status',
    },
    {
      field: 'allocatedOnFieldOn',
      title: 'Allocated On',
    },
  ]

  // * Attendance
  const headCellsAttendance = [
    {
      field: 'date',
      title: 'Date',
    },
    {
      field: 'punchInTime',
      title: 'Punch In Time',
    },
    {
      field: 'punchInLocation',
      title: 'Punch In Location',
      render: (rowData) => rowData.punchInLocation?.address || 'N/A',
    },
    {
      field: 'punchOutTime',
      title: 'Punch Out Time',
    },
    {
      field: 'punchOutLocation',
      title: 'Punch Out Location',
      render: (rowData) => rowData.punchOutLocation?.address || 'N/A',
    },
  ]

  // * Customers
  const headCells = [
    {
      field: 'name',
      title: 'Name',
    },
    {
      field: 'action',
      title: 'Action',
      render: (rowData) => (
        <DeleteOutline
          className='cursor-pointer'
          onClick={() => openDeleteCustomer(rowData._id)}
        />
      ),
    },
  ]

  const processedAttendance = React.useMemo(() => {
    if (!attenance || attenance.length === 0) return [];

    return attenance.flatMap(day => {
      const punchInsAndOuts = day.punchIns.map((punchIn, index) => ({
        date: day.date,
        punchInTime: punchIn.time,
        punchInLocation: punchIn,
        punchOutTime: day.punchOuts[index] ? day.punchOuts[index].time : 'N/A',
        punchOutLocation: day.punchOuts[index] || { address: 'N/A' },
      }));

      // Add any remaining punch-outs if there are more punch-outs than punch-ins
      if (day.punchOuts.length > day.punchIns.length) {
        for (let i = day.punchIns.length; i < day.punchOuts.length; i++) {
          punchInsAndOuts.push({
            date: day.date,
            punchInTime: 'N/A',
            punchInLocation: { address: 'N/A' },
            punchOutTime: day.punchOuts[i].time,
            punchOutLocation: day.punchOuts[i],
          });
        }
      }

      return punchInsAndOuts;
    });
  }, [attenance]);

  // * Delete customer
  const openDeleteCustomer = (item) => {
    setDeleteCustomerModal(true)
    setDeleteCustomerID(item)
  }

  const closeDeleteCustomer = () => {
    setDeleteCustomerModal(false)
    setDeleteCustomerID('')
  }

  const deleteCustomerHandler = () => {
    dispatch(removeCustomer(id, deleteCustomerID))
  }

  const removeCustomerFromUserInfo = useSelector(
    (state) => state.removeCustomerFromUserInfo
  )
  const {
    loadingRemoveCustomerFromUserInfo,
    errorRemoveCustomerFromUserInfo,
    removeCustomerFromUserData,
  } = removeCustomerFromUserInfo

  useEffect(() => {
    dispatch({ type: REMOVE_CUSTOMER_FROM_USER_RESET })
    if (removeCustomerFromUserData) {
      toast('Customer removed successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setTimeout(() => {
        dispatch(getUserByID(id))
        closeDeleteCustomer()
      }, 1000)
    } else if (errorRemoveCustomerFromUserInfo) {
      toast(errorRemoveCustomerFromUserInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [removeCustomerFromUserData, errorRemoveCustomerFromUserInfo, dispatch, id])

  // * Add customer
  const closeAddCustomer = () => {
    setAddCustomerModal(false)
    setAddCustomerName('')
  }

  const addCustomerHandler = () => {
    if (!addCustomerName) {
      toast('Please enter a valid name for the customer', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    } else {
      dispatch(addCustomer(id, addCustomerName))
    }
  }

  const addCustomerToUserInfo = useSelector(
    (state) => state.addCustomerToUserInfo
  )
  const {
    loadingAddCustomerToUserInfo,
    errorAddCustomerToUserInfo,
    addCustomerToUserData,
  } = addCustomerToUserInfo

  useEffect(() => {
    dispatch({ type: ADD_CUSTOMER_TO_USER_RESET })
    if (addCustomerToUserData) {
      toast('Customer added successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setTimeout(() => {
        dispatch(getUserByID(id))
        closeAddCustomer()
      }, 1000)
    } else if (errorAddCustomerToUserInfo) {
      toast(errorAddCustomerToUserInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [addCustomerToUserData, errorAddCustomerToUserInfo, dispatch, id])

  // * Affiliate Management Functions
  const fetchPotentialAffiliates = async () => {
    try {
      setLoadingAffiliates(true)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/affiliates/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo.token}`,
        },
      })
      const data = await response.json()
      if (data.success) {
        // Filter out current user from options
        const filtered = data.affiliates.filter(a => a._id !== id)
        setAffiliateOptions(filtered)
      }
      setLoadingAffiliates(false)
    } catch (error) {
      console.error('Error fetching affiliates:', error)
      setLoadingAffiliates(false)
    }
  }

  const handleAssignAffiliate = async () => {
    if (!selectedAffiliate) {
      toast('Please select an affiliate', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    try {
      setAssigningAffiliate(true)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${id}/affiliate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify({ affiliateId: selectedAffiliate }),
      })
      const data = await response.json()
      if (data.success) {
        toast('Affiliate assigned successfully', {
          type: 'success',
          hideProgressBar: true,
          autoClose: 2000,
        })
        setReferredBy(data.user.referredBy)
      } else {
        toast(data.message || 'Failed to assign affiliate', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
      }
      setAssigningAffiliate(false)
    } catch (error) {
      console.error('Error assigning affiliate:', error)
      toast('Failed to assign affiliate', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setAssigningAffiliate(false)
    }
  }

  const handleRemoveAffiliate = async () => {
    try {
      setAssigningAffiliate(true)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${id}/affiliate`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo.token}`,
        },
      })
      const data = await response.json()
      if (data.success) {
        toast('Affiliate removed successfully', {
          type: 'success',
          hideProgressBar: true,
          autoClose: 2000,
        })
        setReferredBy(null)
        setSelectedAffiliate('')
        setRemoveAffiliateModal(false)
      } else {
        toast(data.message || 'Failed to remove affiliate', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
      }
      setAssigningAffiliate(false)
    } catch (error) {
      console.error('Error removing affiliate:', error)
      toast('Failed to remove affiliate', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setAssigningAffiliate(false)
    }
  }

  // * Remove a single club from the assignment list
  const removeClubHandler = () => {
    const newClubs = assignedClubs.filter(c => c._id !== deleteClubId)
    const newClubIds = newClubs.map(c => c._id)
    // Call the appropriate update function based on user type
    if (isAdmin) {
      dispatch(updateAdminUser(id, name, role, email, newClubIds))
    } else {
      dispatch(updateUser(id, name, role, calling, email, ipAddress, distanceForCalling, newClubIds))
    }
    setAssignedClubs(newClubs)
    setDeleteClubModal(false)
    setDeleteClubId('')
    toast('Club removed successfully', {
      type: 'success',
      hideProgressBar: true,
      autoClose: 2000,
    })
  }

  // * Save the club selection from the edit modal
  const saveClubsHandler = () => {
    // Call the appropriate update function based on user type
    if (isAdmin) {
      dispatch(updateAdminUser(id, name, role, email, selectedClubsForEdit))
    } else {
      dispatch(updateUser(id, name, role, calling, email, ipAddress, distanceForCalling, selectedClubsForEdit))
    }
    // Update local state
    const newAssignedClubs = allClubs.filter(c => selectedClubsForEdit.includes(c._id))
    setAssignedClubs(newAssignedClubs)
    setEditClubsModal(false)
    toast('Clubs updated successfully', {
      type: 'success',
      hideProgressBar: true,
      autoClose: 2000,
    })
  }

  const openEditClubs = (selected) => {
    setSelectedClubsForEdit(selected)
    setEditClubsModal(true)
    dispatch(listClubs())
  }

  if (loading || loadingGetUserByIDInfo) {
    return <Loading />
  }

  const canManageUser = updateUserOption
  const isSuperAdminRole = isAdmin && selectedRoleDetails?.isSuperAdmin === true
  const adminOnline = isAdmin && isAdminOnline(getUserByIDData)
  const lastLogin = getUserByIDData?.lastLogin
  const verification = getUserByIDData?.verification
  const deviceLabel = deviceInfo
    ? `${deviceInfo.brand || ''} ${deviceInfo.model || ''}`.trim()
    : ''
  const callingActive = calling === true || calling === 'true'
  const clubsNeedAssigning = isAdmin && adminRoleRequiresClubs && assignedClubs.length === 0

  const tabItems = [
    { value: 'profile', label: 'Profile', icon: UserRound },
    {
      value: 'clubs',
      label: 'Clubs',
      icon: Building2,
      count: isSuperAdminRole ? undefined : assignedClubs.length,
    },
    ...(!isAdmin ? [{ value: 'affiliate', label: 'Affiliate', icon: Users }] : []),
  ]

  return (
    <Page>
      <div>
        <button
          type='button'
          onClick={() => navigate(-1)}
          className='inline-flex items-center gap-1.5 text-[13px] font-semibold text-cm-muted hover:text-cm-text'
        >
          <ArrowLeft size={15} aria-hidden='true' />
          Back
        </button>
      </div>

      {/* Identity */}
      <Card padded={false}>
        <div className='flex flex-col gap-4 p-4 sm:p-5 md:flex-row md:items-center md:justify-between'>
          <div className='flex items-center gap-4 min-w-0'>
            <Avatar name={name} size={64} />
            <div className='min-w-0'>
              <div className='flex flex-wrap items-center gap-2'>
                <h1 className='font-display text-xl sm:text-2xl font-extrabold text-cm-text m-0 leading-tight break-words'>
                  {name || 'Unnamed'}
                </h1>
                {isAdmin ? (
                  <StatusPill
                    status={adminOnline ? 'Online' : 'Offline'}
                    tone={adminOnline ? 'success' : 'neutral'}
                    size='sm'
                  />
                ) : (
                  <StatusPill
                    status={status === true ? 'Active' : 'Resigned'}
                    tone={status === true ? 'success' : 'danger'}
                    size='sm'
                  />
                )}
              </div>
              <p className='text-sm text-cm-muted m-0 mt-0.5 break-all'>{email || '—'}</p>
              <div className='flex flex-wrap items-center gap-1.5 mt-2'>
                {roleName && <Badge tone='gold'>{roleName}</Badge>}
                {isAdmin && adminLoginAccessLabel && <Badge>{adminLoginAccessLabel}</Badge>}
                {deviceLabel && (
                  <Badge>
                    <Smartphone size={11} className='mr-1' aria-hidden='true' />
                    {deviceLabel}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          {!isAdmin && canManageUser && status === true && (
            <div className='flex flex-wrap gap-2'>
              <Button
                variant='danger'
                size='sm'
                icon={UserX}
                disabled={!updateUserOption || loadingResignUser}
                onClick={() => setOpenResignModal(true)}
              >
                Resign
              </Button>
            </div>
          )}
        </div>

        <dl className='grid grid-cols-2 lg:grid-cols-4 gap-4 m-0 px-4 py-4 sm:px-5 border-t border-cm-border'>
          {isAdmin ? (
            <>
              <KeyValue label='Club scope'>{adminClubScopeLabel}</KeyValue>
              <KeyValue label='Login access'>{adminLoginAccessLabel}</KeyValue>
              <KeyValue label='Joined on'>{formatWhen(joinedOn)}</KeyValue>
              <KeyValue label='Last login'>
                {lastLogin ? (
                  <>
                    {formatWhen(lastLogin, true)}
                    <span className='block text-xs text-cm-muted'>{moment(lastLogin).fromNow()}</span>
                  </>
                ) : (
                  'Never'
                )}
              </KeyValue>
            </>
          ) : (
            <>
              <KeyValue label='Identifier'>{identifier || '—'}</KeyValue>
              <KeyValue label='Role'>{roleName || '—'}</KeyValue>
              <KeyValue label='Joined on'>{formatWhen(joinedOn)}</KeyValue>
              <KeyValue label='Resigned on'>
                {status === true ? 'Still active' : formatWhen(resignedOn)}
              </KeyValue>
            </>
          )}
        </dl>

        {verification && (
          <div className='flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-4 sm:px-5 border-t border-cm-border'>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='text-xs font-semibold text-cm-muted mr-1'>Verification</span>
              {[
                ['Aadhaar', verification.aadhaar?.verified],
                ['PAN', verification.pan?.verified],
                ['Bank', verification.bank?.verified],
              ].map(([label, verified]) => (
                <StatusPill key={label} tone={verified ? 'success' : 'warning'} size='sm'>
                  {label}: {verified ? 'Verified' : 'Pending'}
                </StatusPill>
              ))}
            </div>
            {getUserByIDData.availableBalance !== undefined && (
              <div className='text-sm'>
                <span className='text-xs font-semibold text-cm-muted mr-2'>Balance</span>
                <Money value={getUserByIDData.availableBalance} className='font-semibold' />
              </div>
            )}
          </div>
        )}
      </Card>

      {clubsNeedAssigning && (
        <Notice
          tone='warning'
          icon={AlertTriangle}
          title='No clubs assigned'
          action={
            updateUserOption && (
              <Button variant='secondary' size='sm' onClick={() => setTab('clubs')}>
                Assign clubs
              </Button>
            )
          }
        >
          This role is club-scoped, so this admin can't see any club data until at least one club is assigned.
        </Notice>
      )}

      <Tabs items={tabItems} value={tab} onChange={setTab} ariaLabel='User sections' />

      {tab === 'profile' && (
        <div className='grid gap-5 lg:grid-cols-3 items-start'>
          {/* Account details */}
          <Card className='lg:col-span-2'>
            <CardHeader
              title='Account details'
              description={
                updateUserOption
                  ? 'Edit the fields and save to apply your changes.'
                  : 'You have view-only access to this account.'
              }
            />
            <div className='grid gap-4 sm:grid-cols-2'>
              <TextInput
                label='Name'
                required
                placeholder='Name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!updateUserOption}
              />
              <TextInput
                label='Email'
                required
                type='email'
                placeholder='Email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!updateUserOption}
              />
              <SelectInput
                label='Role'
                required
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder={roleOptions.length ? undefined : 'Select role'}
                options={roleOptions.map((item) => ({ value: item.id, label: item.title }))}
                disabled={!updateUserOption}
                help={
                  isAdmin && selectedRoleDetails
                    ? selectedRoleDetails.isSuperAdmin === true
                      ? 'Super admin: access to every club.'
                      : 'Club-scoped: sees only assigned clubs.'
                    : undefined
                }
              />
              {!isAdmin && (
                <SelectInput
                  label='Calling'
                  required
                  value={String(callingActive)}
                  onChange={(e) => setCalling(e.target.value === 'true')}
                  options={[
                    { value: 'true', label: 'Active' },
                    { value: 'false', label: 'Not active' },
                  ]}
                  disabled={!updateUserOption}
                />
              )}
              {!isAdmin && (
                <SelectInput
                  label='Distance for calling'
                  required
                  value={
                    DISTANCE_OPTIONS.some((o) => Number(o.value) === Number(distanceForCalling))
                      ? String(Number(distanceForCalling))
                      : ''
                  }
                  onChange={(e) => setDistanceForCalling(Number(e.target.value))}
                  placeholder='Select distance'
                  options={DISTANCE_OPTIONS}
                  disabled={!updateUserOption}
                />
              )}
              {!isAdmin && (
                <TextInput
                  label='IP address'
                  required
                  placeholder='IP address'
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  disabled={!updateUserOption}
                />
              )}
            </div>
            {canManageUser && (
              <div className='flex justify-end mt-5 pt-4 border-t border-cm-border'>
                <Button
                  icon={Save}
                  loading={loadingUpdateUserInfo}
                  disabled={!updateUserOption}
                  onClick={updateUserHandler}
                >
                  Save changes
                </Button>
              </div>
            )}
          </Card>

          {/* Security */}
          <Card>
            <CardHeader title='Security' description='Credentials and the device tied to this account.' />
            <div className='flex flex-col divide-y divide-cm-border'>
              <div className='flex items-center justify-between gap-3 pb-4'>
                <div className='flex items-center gap-3 min-w-0'>
                  <IconChip icon={KeyRound} tone='gold' size={36} />
                  <div className='min-w-0'>
                    <p className='text-sm font-semibold text-cm-text m-0'>Password</p>
                    <p className='text-xs text-cm-muted m-0'>Set a new login password.</p>
                  </div>
                </div>
                <Button
                  variant='secondary'
                  size='sm'
                  disabled={!updateUserOption || isSettingPassword}
                  onClick={openPasswordModal}
                >
                  Set password
                </Button>
              </div>

              <div className='pt-4'>
                <div className='flex items-center justify-between gap-3'>
                  <div className='flex items-center gap-3 min-w-0'>
                    <IconChip icon={Smartphone} tone={adminHasRegisteredDevice || !isAdmin ? 'info' : 'neutral'} size={36} />
                    <div className='min-w-0'>
                      <p className='text-sm font-semibold text-cm-text m-0'>
                        {isAdmin ? 'App device' : 'Device'}
                      </p>
                      <p className='text-xs text-cm-muted m-0 truncate'>
                        {deviceLabel || (isAdmin && !adminHasRegisteredDevice
                          ? 'No device registered'
                          : 'No device details')}
                      </p>
                    </div>
                  </div>
                  {(!isAdmin || adminHasRegisteredDevice) && (
                    <Button
                      variant='secondary'
                      size='sm'
                      icon={RotateCcw}
                      disabled={!updateUserOption || loadingResetDeviceIDInfo}
                      onClick={resetDeviceIDHandler}
                    >
                      Reset
                    </Button>
                  )}
                </div>
                {isAdmin && adminHasRegisteredDevice && (
                  <dl className='grid gap-3 m-0 mt-3 rounded-cm bg-cm-surface-2 p-3'>
                    <KeyValue label='Device ID'>
                      <span className='font-mono text-xs break-all'>{adminDeviceId || '—'}</span>
                    </KeyValue>
                    {adminDeviceDetails?.platform && (
                      <KeyValue label='Platform'>{adminDeviceDetails.platform}</KeyValue>
                    )}
                  </dl>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === 'clubs' && (
        <Card>
          <CardHeader
            title='Assigned clubs'
            description={
              isSuperAdminRole
                ? 'Super admins can access every club. Assigning clubs is optional.'
                : 'Clubs this account can see and manage.'
            }
            actions={
              updateUserOption && assignedClubs.length > 0 && (
                <Button
                  variant='secondary'
                  size='sm'
                  icon={Pencil}
                  onClick={() => openEditClubs(assignedClubs.map(c => c._id))}
                >
                  Edit clubs
                </Button>
              )
            }
          />
          {assignedClubs.length > 0 ? (
            <ul className='grid gap-3 sm:grid-cols-2 xl:grid-cols-3 m-0 p-0 list-none'>
              {assignedClubs.map((club, index) => (
                <li
                  key={club._id || index}
                  className='flex items-center gap-3 rounded-cm-lg border border-cm-border bg-cm-surface-2 p-3 min-w-0'
                >
                  <Avatar name={club.name || 'Club'} size={36} />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-semibold text-cm-text m-0 truncate'>{club.name || 'Unknown club'}</p>
                    {club.description && (
                      <p className='text-xs text-cm-muted m-0 truncate'>{club.description}</p>
                    )}
                  </div>
                  {updateUserOption && (
                    <IconButton
                      icon={Trash2}
                      label={`Remove ${club.name || 'club'}`}
                      variant='ghost'
                      size='sm'
                      onClick={() => {
                        setDeleteClubId(club._id)
                        setDeleteClubModal(true)
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              compact
              icon={Building2}
              title={isSuperAdminRole ? 'All clubs' : 'No clubs assigned'}
              description={
                isSuperAdminRole
                  ? 'This super admin already has access to every club.'
                  : 'Assign clubs so this account can see their tables, players and reports.'
              }
              action={
                updateUserOption && (
                  <Button size='sm' icon={Plus} onClick={() => openEditClubs([])}>
                    Assign clubs
                  </Button>
                )
              }
            />
          )}
        </Card>
      )}

      {tab === 'affiliate' && !isAdmin && (
        <Card>
          <CardHeader title='Affiliate' description='The affiliate who referred this user.' />
          {referredBy ? (
            <div className='flex flex-wrap items-center justify-between gap-3 rounded-cm-lg border border-cm-border bg-cm-surface-2 p-3 mb-5'>
              <PersonCell
                name={referredBy.name || referredBy.identifier || 'Unknown'}
                sub={[referredBy.phoneNumber, referredBy.referralCode].filter(Boolean).join(' • ')}
                size={40}
              />
              {updateUserOption && (
                <Button
                  variant='danger'
                  size='sm'
                  disabled={assigningAffiliate}
                  onClick={() => setRemoveAffiliateModal(true)}
                >
                  Remove
                </Button>
              )}
            </div>
          ) : (
            !updateUserOption && (
              <p className='text-sm text-cm-muted m-0'>No affiliate assigned to this user.</p>
            )
          )}

          {updateUserOption && (
            <div className='flex flex-col sm:flex-row sm:items-end gap-3'>
              <SelectInput
                className='w-full sm:w-80'
                label={referredBy ? 'Change affiliate' : 'Assign affiliate'}
                value={selectedAffiliate}
                onChange={(e) => setSelectedAffiliate(e.target.value)}
                onClick={() => {
                  if (affiliateOptions.length === 0) fetchPotentialAffiliates()
                }}
                onFocus={() => {
                  if (affiliateOptions.length === 0) fetchPotentialAffiliates()
                }}
                disabled={loadingAffiliates || assigningAffiliate}
              >
                <option value=''>Select affiliate</option>
                {loadingAffiliates ? (
                  <option disabled>Loading affiliates…</option>
                ) : (
                  affiliateOptions.map((affiliate) => (
                    <option key={affiliate._id} value={affiliate._id}>
                      {affiliate.displayLabel || `${affiliate.name} (${affiliate.referralCode})`}
                    </option>
                  ))
                )}
              </SelectInput>
              <Button
                loading={assigningAffiliate}
                disabled={!selectedAffiliate || selectedAffiliate === referredBy?._id}
                onClick={handleAssignAffiliate}
              >
                {referredBy ? 'Change affiliate' : 'Assign affiliate'}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Legacy panels: no tab opens these any more */}
      {showLocations && (
        <div className='flex flex-row gap-5' style={{ height: 480 }}>
          <div
            className='flex flex-col gap-3'
            style={{
              width: '30%',
              backgroundColor: 'white',
              borderRadius: 5,
              padding: 16,
            }}
          >
            <div className='flex flex-col'>
              <label className='text-sm mb-2' htmlFor='name'>
                Date
              </label>
              <Input
                value={date}
                onChange={(e) => setDate(e.target.value)}
                type='date'
              />
            </div>
            <LegacyButton text='Search' custom='py-2' onClick={searchDate} />
            <div className='flex flex-row items-center gap-2'>
              <p className='text-sm'>Date:</p>
              <p className='text-sm font-semibold'>
                {attendanceDetails && attendanceDetails.attendance.date}
              </p>
            </div>
            <div className='flex flex-row items-center gap-2'>
              <p className='text-sm'>In Time:</p>
              <p className='text-sm font-semibold'>
                {attendanceDetails &&
                  attendanceDetails.attendance.punchInTime}
              </p>
            </div>
            <div className='flex flex-col'>
              <p className='text-sm'>In Location:</p>
              <p className='text-sm font-semibold'>
                {attendanceDetails &&
                  attendanceDetails.attendance.punchInLocation.address}
              </p>
            </div>
            {attendanceDetails && attendanceDetails.attendance.punchOutTime && (
              <>
                <div className='flex flex-row items-center gap-2'>
                  <p className='text-sm'>Out Time:</p>
                  <p className='text-sm font-semibold'>
                    {attendanceDetails &&
                      attendanceDetails.attendance.punchOutTime}
                  </p>
                </div>
                <div className='flex flex-col'>
                  <p className='text-sm'>Out Location:</p>
                  <p className='text-sm font-semibold'>
                    {attendanceDetails &&
                      attendanceDetails.attendance.punchInLocation.address}
                  </p>
                </div>
              </>
            )}
            <div className='flex flex-row items-center gap-2'>
              <p className='text-sm'>Total Tasks:</p>
              <p className='text-sm font-semibold'>
                {attendanceDetails && attendanceDetails.tasks}
              </p>
            </div>
          </div>
        </div>
      )}
      {showTasks && (
        <MaterialTable
          icons={tableIcons}
          title={'Tasks'}
          columns={headCellsTasks}
          data={tasks}
          options={{
            exportButton: false,
            search: true,
            exportAllData: false,
            rowStyle: {
              height: '5px',
              fontSize: 13,
            },
            paging: true,
            pageSize: 10,
            emptyRowsWhenPaging: false,
            pageSizeOptions: [10, 20, 50],
            headerStyle: {
              position: 'sticky',
              top: '0',
            },
          }}
        />
      )}
      {showAttendance && (
        <MaterialTable
          icons={tableIcons}
          title={'Attendance'}
          columns={headCellsAttendance}
          data={processedAttendance}
          options={{
            exportButton: true,
            exportAllData: true,
            search: true,
            rowStyle: {
              fontSize: 13,
            },
            paging: true,
            pageSize: 10,
            emptyRowsWhenPaging: false,
            pageSizeOptions: [10, 20, 50],
            headerStyle: {
              position: 'sticky',
              top: '0',
            },
            grouping: true,
            filtering: true,
          }}
        />
      )}
      {showCustomers && (
        <MaterialTable
          icons={tableIcons}
          title={'Customers'}
          columns={headCells}
          data={userCustomers}
          options={{
            exportButton: false,
            search: true,
            exportAllData: false,
            rowStyle: {
              height: '5px',
              fontSize: 13,
            },
            paging: true,
            pageSize: 10,
            emptyRowsWhenPaging: false,
            pageSizeOptions: [10, 20, 50],
            headerStyle: {
              position: 'sticky',
              top: '0',
            },
          }}
          actions={[
            {
              icon: () => (
                <LegacyButton
                  onClick={() => setAddCustomerModal(true)}
                  text="Add Customer"
                  custom="ml-2"
                />
              ),
              isFreeAction: true,
            },
          ]}
        />
      )}

      {/* * Modals */}
      <Modal
        open={passwordModal}
        onClose={isSettingPassword ? undefined : closePasswordModal}
        title='Set new password'
        description={`Choose a new login password for ${name}.`}
        icon={KeyRound}
        size='sm'
        footer={
          <>
            <Button variant='secondary' onClick={closePasswordModal} disabled={isSettingPassword}>
              Cancel
            </Button>
            <Button
              loading={isSettingPassword}
              disabled={!newPassword || newPassword.trim().length < 6}
              onClick={submitPasswordHandler}
            >
              Save password
            </Button>
          </>
        }
      >
        <TextInput
          label='New password'
          required
          type={showPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder='Minimum 6 characters'
          disabled={isSettingPassword}
          help='Must be at least 6 characters long.'
          data-autofocus
          suffix={
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className='grid place-items-center text-cm-muted hover:text-cm-text'
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />
      </Modal>

      <Modal
        open={!isAdmin && openResignModal}
        onClose={() => setOpenResignModal(false)}
        title='Resign this employee?'
        description={`${name} will be marked as resigned.`}
        icon={UserX}
        tone='danger'
        size='sm'
        footer={
          <>
            <Button variant='secondary' onClick={() => setOpenResignModal(false)}>
              Cancel
            </Button>
            <Button variant='danger' loading={loadingResignUser} onClick={resignHandler}>
              Resign
            </Button>
          </>
        }
      />

      <Modal
        open={removeAffiliateModal}
        onClose={() => setRemoveAffiliateModal(false)}
        title='Remove affiliate?'
        description={`${name} will no longer be linked to an affiliate.`}
        icon={Trash2}
        tone='danger'
        size='sm'
        footer={
          <>
            <Button variant='secondary' onClick={() => setRemoveAffiliateModal(false)} disabled={assigningAffiliate}>
              Cancel
            </Button>
            <Button variant='danger' loading={assigningAffiliate} onClick={handleRemoveAffiliate}>
              Remove affiliate
            </Button>
          </>
        }
      >
        {referredBy && (
          <div className='rounded-cm bg-cm-surface-2 p-3'>
            <PersonCell
              name={referredBy.name || referredBy.identifier}
              sub={[referredBy.phoneNumber, referredBy.referralCode].filter(Boolean).join(' • ')}
            />
          </div>
        )}
      </Modal>

      <Modal
        open={deleteCustomerModal}
        onClose={closeDeleteCustomer}
        title='Delete customer?'
        description='This customer will be removed from the user.'
        icon={Trash2}
        tone='danger'
        size='sm'
        footer={
          <>
            <Button variant='secondary' onClick={closeDeleteCustomer}>
              Cancel
            </Button>
            <Button variant='danger' loading={loadingRemoveCustomerFromUserInfo} onClick={deleteCustomerHandler}>
              Delete
            </Button>
          </>
        }
      />

      <Modal
        open={addCustomerModal}
        onClose={closeAddCustomer}
        title='Add customer'
        size='sm'
        footer={
          <>
            <Button variant='secondary' onClick={closeAddCustomer}>
              Cancel
            </Button>
            <Button loading={loadingAddCustomerToUserInfo} onClick={addCustomerHandler}>
              Add
            </Button>
          </>
        }
      >
        <SelectInput
          label='Customer'
          required
          value={addCustomerName}
          onChange={(e) => setAddCustomerName(e.target.value)}
          placeholder='Select customer'
          options={customerOptions.map((item) => ({ value: item.id, label: item.title }))}
        />
      </Modal>

      <Modal
        open={deleteClubModal}
        onClose={() => {
          setDeleteClubModal(false)
          setDeleteClubId('')
        }}
        title='Remove club?'
        description={`${
          assignedClubs.find(c => c._id === deleteClubId)?.name || 'This club'
        } will be removed from ${name}'s assigned clubs.`}
        icon={Trash2}
        tone='danger'
        size='sm'
        footer={
          <>
            <Button
              variant='secondary'
              onClick={() => {
                setDeleteClubModal(false)
                setDeleteClubId('')
              }}
            >
              Cancel
            </Button>
            <Button variant='danger' onClick={removeClubHandler}>
              Remove
            </Button>
          </>
        }
      />

      <Modal
        open={editClubsModal}
        onClose={() => setEditClubsModal(false)}
        title='Edit assigned clubs'
        description='Select the clubs this account can access.'
        icon={Building2}
        footer={
          <>
            <span className='mr-auto self-center text-sm text-cm-muted'>
              {selectedClubsForEdit.length} selected
            </span>
            <Button variant='secondary' onClick={() => setEditClubsModal(false)}>
              Cancel
            </Button>
            <Button disabled={selectedClubsForEdit.length === 0} onClick={saveClubsHandler}>
              Save
            </Button>
          </>
        }
      >
        {allClubs.length > 0 ? (
          <div className='flex flex-col gap-2'>
            {allClubs.map((club) => {
              const checked = selectedClubsForEdit.includes(club._id)
              return (
                <label
                  key={club._id}
                  className={`flex items-center gap-3 rounded-cm-lg border p-3 cursor-pointer transition-colors ${
                    checked
                      ? 'border-cm-gold bg-cm-gold-soft'
                      : 'border-cm-border bg-cm-surface-2 hover:border-cm-border-strong'
                  }`}
                >
                  <input
                    type='checkbox'
                    checked={checked}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedClubsForEdit([...selectedClubsForEdit, club._id])
                      } else {
                        setSelectedClubsForEdit(selectedClubsForEdit.filter(id => id !== club._id))
                      }
                    }}
                    className='w-4 h-4 accent-[var(--cm-gold)] cursor-pointer'
                  />
                  <Avatar name={club.name || 'Club'} size={32} />
                  <div className='min-w-0'>
                    <p className='text-sm font-semibold text-cm-text m-0 truncate'>{club.name}</p>
                    {club.description && (
                      <p className='text-xs text-cm-muted m-0 truncate'>{club.description}</p>
                    )}
                  </div>
                </label>
              )
            })}
          </div>
        ) : (
          <EmptyState compact icon={Building2} title='No clubs available' />
        )}
      </Modal>
    </Page>
  )
}

export default UserByID
